import React, { useEffect, useRef, useState } from "react";
import { DetectionZone, LaneLine, ScreenLine, Facility } from "@app/shared";
import { calculateDistance, calculateBearing } from "../../../helpers/snapToGrid";
import { useFacilityCoordsHelper } from "../../../helpers";

interface MapMeasurementsProps {
    map: google.maps.Map | null | undefined;
    enabled: boolean;
    shapes: (DetectionZone | LaneLine | ScreenLine)[];
    facility: Facility;
    showDynamicMeasurement?: boolean;
}

interface MeasurementLabel {
    position: google.maps.LatLng;
    text: string;
}

export const MapMeasurements: React.FC<MapMeasurementsProps> = ({ map, enabled, shapes, facility, showDynamicMeasurement = false }) => {
    const overlaysRef = useRef<google.maps.OverlayView[]>([]);
    const coordsHelper = useFacilityCoordsHelper(facility);
    const [cursorPosition, setCursorPosition] = useState<google.maps.LatLng | null>(null);
    const mouseMoveListenerRef = useRef<google.maps.MapsEventListener | null>(null);

    useEffect(() => {
        const cleanupMouseListener = () => {
            if (mouseMoveListenerRef.current) {
                mouseMoveListenerRef.current.remove();
                mouseMoveListenerRef.current = null;
            }
        };

        if (!map || !showDynamicMeasurement) {
            cleanupMouseListener();
            setCursorPosition(null);
            return;
        }

        mouseMoveListenerRef.current = map.addListener("mousemove", (e: google.maps.MapMouseEvent) => {
            if (e.latLng) {
                setCursorPosition(e.latLng);
            }
        });

        return cleanupMouseListener;
    }, [map, showDynamicMeasurement]);

    useEffect(() => {
        const cleanup = () => {
            overlaysRef.current.forEach((overlay) => overlay.setMap(null));
            overlaysRef.current = [];
        };

        if (!map || !enabled || !coordsHelper || shapes.length === 0) {
            cleanup();
            return;
        }

        const labels: MeasurementLabel[] = [];

        shapes.forEach((shape) => {
            if (!shape.points || shape.points.length < 2) {
                return;
            }

            const vertices = shape.points.map((p) => coordsHelper.toLatLng(p));

            for (let i = 0; i < vertices.length - 1; i++) {
                const p1 = vertices[i];
                const p2 = vertices[i + 1];

                const distance = calculateDistance(p1, p2);
                const bearing = calculateBearing(p1, p2);

                // Calculate midpoint for label placement
                const midLat = (p1.lat() + p2.lat()) / 2;
                const midLng = (p1.lng() + p2.lng()) / 2;
                const midpoint = new google.maps.LatLng(midLat, midLng);

                labels.push({
                    position: midpoint,
                    text: `${distance.toFixed(2)}m @ ${bearing.toFixed(0)}°`,
                });
            }

            const isDetectionZone = shape.$type?.includes("DetectionZone");
            if (isDetectionZone && vertices.length > 2) {
                const p1 = vertices[vertices.length - 1];
                const p2 = vertices[0];

                const distance = calculateDistance(p1, p2);
                const bearing = calculateBearing(p1, p2);

                const midLat = (p1.lat() + p2.lat()) / 2;
                const midLng = (p1.lng() + p2.lng()) / 2;
                const midpoint = new google.maps.LatLng(midLat, midLng);

                labels.push({
                    position: midpoint,
                    text: `${distance.toFixed(2)}m @ ${bearing.toFixed(0)}°`,
                });
            }
        });

        if (showDynamicMeasurement && cursorPosition && shapes.length > 0) {
            const currentShape = shapes[0];

            if (currentShape && currentShape.points && currentShape.points.length > 0) {
                const lastPoint = currentShape.points[currentShape.points.length - 1];
                const lastVertex = coordsHelper.toLatLng(lastPoint);

                const distance = calculateDistance(lastVertex, cursorPosition);
                const bearing = calculateBearing(lastVertex, cursorPosition);

                const midLat = (lastVertex.lat() + cursorPosition.lat()) / 2;
                const midLng = (lastVertex.lng() + cursorPosition.lng()) / 2;
                const midpoint = new google.maps.LatLng(midLat, midLng);

                labels.push({
                    position: midpoint,
                    text: `${distance.toFixed(2)}m @ ${bearing.toFixed(0)}°`,
                });
            }
        }

        class TextOverlay extends google.maps.OverlayView {
            private position: google.maps.LatLng;
            private text: string;
            private div: HTMLDivElement | null = null;

            constructor(position: google.maps.LatLng, text: string) {
                super();
                this.position = position;
                this.text = text;
            }

            onAdd() {
                this.div = document.createElement("div");
                this.div.style.position = "absolute";
                this.div.style.backgroundColor = "rgba(0, 0, 0, 0.75)";
                this.div.style.color = "white";
                this.div.style.padding = "4px 8px";
                this.div.style.borderRadius = "4px";
                this.div.style.fontSize = "12px";
                this.div.style.fontWeight = "bold";
                this.div.style.whiteSpace = "nowrap";
                this.div.style.pointerEvents = "none";
                this.div.style.zIndex = "1000";
                this.div.textContent = this.text;

                const panes = this.getPanes();
                panes?.overlayLayer.appendChild(this.div);
            }

            draw() {
                if (!this.div) return;

                const overlayProjection = this.getProjection();
                const pos = overlayProjection.fromLatLngToDivPixel(this.position);

                if (pos) {
                    this.div.style.left = pos.x + "px";
                    this.div.style.top = pos.y + "px";
                    this.div.style.transform = "translate(-50%, -50%)";
                }
            }

            onRemove() {
                if (this.div) {
                    this.div.parentNode?.removeChild(this.div);
                    this.div = null;
                }
            }
        }

        const newOverlays = labels.map((label) => {
            const overlay = new TextOverlay(label.position, label.text);
            overlay.setMap(map);
            return overlay;
        });

        overlaysRef.current = newOverlays;

        return cleanup;
    }, [map, enabled, shapes, coordsHelper, showDynamicMeasurement, cursorPosition]);

    return null;
};
